import { useState, useEffect, useCallback } from 'react';
import { api } from '../config/api';
import {
  UserWithPermissions,
  CreateUserRequest,
  UpdatePermissionsRequest,
  ListUsersResponse,
} from '../types/user.types';

interface UseUsersOptions {
  role?: string;
  isActive?: boolean;
  searchTerm?: string;
  autoFetch?: boolean;
}

export const useUsers = (options: UseUsersOptions = {}) => {
  const [users, setUsers] = useState<UserWithPermissions[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (options.role) params.append('role', options.role);
      if (options.isActive !== undefined) params.append('isActive', String(options.isActive));
      if (options.searchTerm) params.append('search', options.searchTerm);

      const response = await api.get<{ success: boolean; data: ListUsersResponse }>(
        `/admin/users?${params.toString()}`
      );

      if (response.data.success && response.data.data) {
        // Transform permissions from objects to strings
        const transformedUsers = response.data.data.users.map(user => ({
          ...user,
          permissions: user.permissions?.map((p: any) =>
            typeof p === 'string' ? p : p.permission
          ) || []
        }));
        setUsers(transformedUsers);
        setTotal(response.data.data.total);
      }
    } catch (err: any) {
      const errData = err.response?.data?.error;
      setError(typeof errData === 'string' ? errData : errData?.message || 'Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, [options.role, options.isActive, options.searchTerm]);

  useEffect(() => {
    if (options.autoFetch !== false) {
      fetchUsers();
    }
  }, [fetchUsers, options.autoFetch]);

  const createUser = useCallback(async (userData: CreateUserRequest): Promise<UserWithPermissions | null> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post<{ success: boolean; data: { user: UserWithPermissions } }>(
        `/admin/users`,
        userData
      );

      await fetchUsers(); // Refresh list
      return response.data.data?.user || null;
    } catch (err: any) {
      const errData = err.response?.data?.error;
      setError(typeof errData === 'string' ? errData : errData?.message || 'Failed to create user');
      console.error('Error creating user:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  const updatePermissions = useCallback(async (
    userId: string,
    data: UpdatePermissionsRequest
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await api.put(
        `/admin/users/${userId}/permissions`,
        data
      );

      await fetchUsers(); // Refresh list
      return true;
    } catch (err: any) {
      const errData = err.response?.data?.error;
      setError(typeof errData === 'string' ? errData : errData?.message || 'Failed to update permissions');
      console.error('Error updating permissions:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  const deleteUser = useCallback(async (userId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await api.delete(`/admin/users/${userId}`);
      await fetchUsers(); // Refresh list
      return true;
    } catch (err: any) {
      const errData = err.response?.data?.error;
      setError(typeof errData === 'string' ? errData : errData?.message || 'Failed to delete user');
      console.error('Error deleting user:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    total,
    fetchUsers,
    createUser,
    updatePermissions,
    deleteUser,
  };
};

export const useUser = (userId: string | null) => {
  const [user, setUser] = useState<UserWithPermissions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.get<{ success: boolean; data: { user: UserWithPermissions } }>(
        `/admin/users/${userId}`
      );

      if (response.data.success && response.data.data) {
        // Transform permissions from objects to strings
        const userData = response.data.data.user;
        setUser({
          ...userData,
          permissions: userData.permissions?.map((p: any) =>
            typeof p === 'string' ? p : p.permission
          ) || []
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch user');
      console.error('Error fetching user:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchUser();
    }
  }, [userId, fetchUser]);

  return {
    user,
    loading,
    error,
    refetch: fetchUser,
  };
};
